<template>
  <section class="screen">
    <step-header step="check-assets" />

    <!-- LIST -->
    <template v-if="!preview">
      <div class="s-body stack gap-12">
        <div class="stack gap-6">
          <h2>بازبینی اطلاعات</h2>
          <p>موارد زیر را ببینید. برای تغییر یا بررسی هر مورد، روی آن بزنید.</p>
        </div>

        <div class="rev">
          <review-card
            v-for="item in items"
            :key="item.kind"
            :kind="item.kind"
            :label="item.label"
            :thumb="item.thumb"
            @inspect="openPreview(item.kind)"
          />
        </div>
      </div>

      <div class="s-foot">
        <p class="consent">«تأیید و ارسال نهایی»، صحت اطلاعات واردشده را تأیید می‌کنید.</p>
        <button class="btn btn-primary" type="button" @click="submit">تأیید و ارسال نهایی</button>
      </div>
    </template>

    <!-- SINGLE-ITEM PREVIEW -->
    <template v-else>
      <div class="s-body stack gap-12">
        <div class="stack gap-6">
          <h2>{{ preview.title }}</h2>
          <p>{{ preview.sub }}</p>
        </div>

        <div class="pvstage" @click="onStageClick">
          <video
            v-if="showVideo"
            ref="pvVideo"
            playsinline
            :src="videoUrl"
            @play="pvPaused = false"
            @pause="pvPaused = true"
            @ended="pvPaused = true"
          ></video>
          <img v-else :src="preview.image" alt="" />

          <button
            v-if="showVideo"
            type="button"
            class="vtoggle"
            :aria-label="pvPaused ? 'پخش ویدیو' : 'توقف ویدیو'"
            @click.stop="toggleVideo"
          >
            <base-icon :name="pvPaused ? 'play' : 'pause'" />
            <span>{{ pvPaused ? "پخش" : "توقف" }}</span>
          </button>
        </div>
      </div>

      <div class="s-foot">
        <button class="btn btn-outline" type="button" @click="recapture">
          <base-icon name="edit" />
          <span>{{ preview.edit }}</span>
        </button>
        <button class="btn btn-primary" type="button" @click="closePreview">
          بازگشت به بازبینی
        </button>
      </div>
    </template>
  </section>
</template>

<script>
import StepHeader from "@/components/StepHeader.vue";
import ReviewCard from "@/components/ReviewCard.vue";
import BaseIcon from "@/components/BaseIcon.vue";

/**
 * Step 4 — review, then submit.
 *
 * Tapping a card opens a read-only preview rather than dropping straight into re-capture.
 * The earlier behaviour discarded the item the moment you looked at it, and most people
 * open a card to check, not to redo; re-capturing is a deliberate second tap.
 *
 * Re-capture routes back to the relevant step with `?edit=1`, which tells that step to
 * return here on confirm instead of advancing, and to hide the step counter — this is not
 * "step 2 of 4" any more.
 */
const PREVIEW_COPY = {
  face: {
    title: "عکس چهره",
    sub: "اگر عکس واضح و مطابق راهنماست، نیازی به تغییر نیست.",
    edit: "گرفتن عکس جدید",
    route: "capture-image",
  },
  video: {
    title: "ویدیوی چهره",
    sub: "ویدیو را ببینید؛ اگر واضح است نیازی به تغییر نیست.",
    edit: "ضبط ویدیوی جدید",
    route: "capture-video",
  },
  signature: {
    title: "امضای الکترونیک",
    sub: "اگر امضا خوانا و کامل است، نیازی به تغییر نیست.",
    edit: "ثبت امضای جدید",
    route: "select-sign",
  },
};

export default {
  name: "CheckAssetsView",
  components: { StepHeader, ReviewCard, BaseIcon },

  data() {
    return {
      /** null, or the kind currently being previewed. */
      previewKind: null,
      videoUrl: "",
      pvPaused: true,
    };
  },

  computed: {
    flow() {
      return this.$store.state.flow;
    },

    items() {
      return [
        { kind: "face", label: "عکس چهره", thumb: this.flow.face },
        { kind: "video", label: "ویدیوی کوتاه", thumb: this.flow.videoPoster },
        { kind: "signature", label: "امضای الکترونیک", thumb: this.flow.signature },
      ];
    },

    preview() {
      if (!this.previewKind) return null;
      const copy = PREVIEW_COPY[this.previewKind];
      return Object.assign({}, copy, { image: this.imageFor(this.previewKind) });
    },

    /**
     * Only play a clip when one is actually loaded. A resumed session may have the poster
     * but no blob, and an empty <video> is a black box with no explanation.
     */
    showVideo() {
      return this.previewKind === "video" && !!this.videoUrl;
    },
  },

  beforeDestroy() {
    this.revokeVideo();
  },

  methods: {
    imageFor(kind) {
      if (kind === "face") return this.flow.face;
      if (kind === "signature") return this.flow.signature;
      return this.flow.videoPoster;
    },

    async openPreview(kind) {
      this.previewKind = kind;
      if (kind !== "video") return;

      const blob = await this.$store.dispatch("flow/loadVideoBlob");
      if (!blob || this.previewKind !== "video") return;
      this.revokeVideo();
      this.videoUrl = URL.createObjectURL(blob);
      this.pvPaused = true;
    },

    closePreview() {
      this.pauseVideo();
      this.revokeVideo();
      this.previewKind = null;
    },

    recapture() {
      const target = this.preview.route;
      this.pauseVideo();
      this.revokeVideo();
      this.$router.push({ name: target, query: { edit: "1" } });
    },

    toggleVideo() {
      const video = this.$refs.pvVideo;
      if (!video) return;
      if (video.paused || video.ended) {
        const playing = video.play();
        if (playing && playing.catch) playing.catch(() => {});
      } else {
        video.pause();
      }
    },

    /** The whole stage is a tap target; the pill handles its own taps. */
    onStageClick(event) {
      if (!this.showVideo) return;
      if (event.target.closest && event.target.closest("button")) return;
      this.toggleVideo();
    },

    pauseVideo() {
      const video = this.$refs.pvVideo;
      if (!video) return;
      try {
        video.pause();
      } catch (e) {
        /* not playing */
      }
    },

    revokeVideo() {
      if (this.videoUrl) {
        URL.revokeObjectURL(this.videoUrl);
        this.videoUrl = "";
      }
    },

    submit() {
      this.$router.push({ name: "complete" });
    },
  },
};
</script>

<style scoped>
/* Three cards divide the available height evenly, so the list never scrolls — scrolling
   a three-item confirmation screen is how people miss the third item. */
.rev {
  display: flex;
  flex-direction: column;
  gap: 12px;
  flex: 1 1 auto;
  min-height: 0;
}

.consent {
  font-size: 12.5px;
  color: var(--muted-foreground);
  text-align: center;
  line-height: 1.65;
  padding: 0 6px 4px;
  margin: 0;
}

.pvstage {
  position: relative;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: var(--muted);
  border: 1px solid var(--border);
  border-radius: 20px;
  overflow: hidden;
}

.pvstage img,
.pvstage video {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

.vtoggle {
  position: absolute;
  bottom: 14px;
  left: 50%;
  transform: translateX(-50%);
  z-index: 5;
  display: inline-flex;
  align-items: center;
  gap: 7px;
  background: rgba(8, 10, 16, 0.74);
  color: #fff;
  border: 0;
  border-radius: 999px;
  padding: 10px 18px;
  font-family: inherit;
  font-size: 13.5px;
  font-weight: 600;
  cursor: pointer;
  backdrop-filter: blur(6px);
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
}

.vtoggle >>> svg {
  width: 16px;
  height: 16px;
  flex: 0 0 auto;
}
</style>
